import React, { useContext, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { useDrag } from 'react-dnd';
import { FlexMiddle, Tooltip, Flex, TextWithTooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import { ReactComponent as BaseConfirmedIcon } from '@ubeya/shared-web/assets/confirmed.svg';
import { ReactComponent as ErrorIcon } from '@ubeya/shared-web/assets/error.svg';
import { DRAG_AND_DROP_OPTIONS } from 'scheduling/drag-drop-constants';

const Wrapper = styled(FlexMiddle)`
  justify-content: center;
  position: relative;
  cursor: pointer;
  width: 100%;
  background: transparent;
`;

const Card = styled(Flex)`
  ${({ theme }) => `border: 1px solid ${theme.colors.grey2};`};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid
    ${({ theme, $isDragging, status }) =>
      $isDragging
        ? theme.colors.grey2
        : status === 'draft'
        ? theme.colors.greenLight
        : status === 'booked' || status === 'arrivalConfirmed'
        ? theme.colors.greenPositive
        : theme.colors.white};

  ${({ $shiftDontEndInCurrentDate }) =>
    $shiftDontEndInCurrentDate &&
    css`
      border-right: unset;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    `}

  ${({ $shiftDontStartInCurrentDate }) =>
    $shiftDontStartInCurrentDate &&
    css`
      border-left: unset;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;

      .status-line {
        display: none;
      }
    `}


  
  ${({ $isDragging, theme }) => $isDragging && `border: 2px dashed ${theme.colors.grey2};`}

  border-radius: 6px;
  flex: 1;
  width: 100%;

  box-shadow: 0 2px 4px 0 rgba(47, 60, 79, 0.08);
  z-index: 1;
  margin: 0 4px;
  position: relative;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6px 0;
  width: 90%;
`;

const ConfirmedIcon = styled(BaseConfirmedIcon)`
  margin-left: 8px;
`;

const PositionDetails = styled.div`
  font-size: 13px;
  margin-bottom: 5px;
  display: flex;
`;

const Label = styled.div`
  color: ${({ $color }) => $color};
  margin-right: 2px;
`;

const StatusLine = styled.div.attrs(() => ({ className: 'status-line' }))`
  width: 8px;
  margin-right: 8px;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;

  background: ${({ theme, $isDragging, status }) =>
    $isDragging
      ? theme.colors.grey2
      : status === 'draft'
      ? theme.colors.greenLight
      : status === 'booked' || status === 'arrivalConfirmed'
      ? theme.colors.greenPositive
      : theme.colors.white};
`;

const Hours = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.disabled};
  font-weight: 300;
`;

const CircleChip = styled(ErrorIcon)`
  width: 12px;
  height: 12px;
  margin: 2px;
`;

const Warning = styled.div`
  position: absolute;
  right: 2px;
  top: 2px;
`;

const PositionLabel = styled(TextWithTooltip)`
  max-width: 100px;
`;

const BookedRenderer = ({
  index,
  date,
  data,
  employeeId,
  bookedOutsideAvailabilityByEmployee,
  mappedLabels,
  t,
  offset,
  onDayClick,

  maxBucket,
  employeesMap
}) => {
  const { timeFormat, isShowLastNameFirst } = useContext(ConfigContext);

  const {
    shiftId,
    slotId,
    projectId,
    positionId,
    startTime,
    endTime,
    labelId,
    position,
    locationId,
    startBucketNum,
    endBucketNum
  } = data;

  const isConfirmed = !!data?.isConfirmed;
  const isArrivalConfirmed = !!data?.isArrivalConfirmed;

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_AND_DROP_OPTIONS.DAILY_BOOKED_SHIFT,
    item: {
      shiftId,
      projectId,
      slotId,
      startTime,
      endTime,
      labelId,
      locationId,
      positionId,
      date,
      currentEmployeeId: employeeId
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  const status = useMemo(() => (isArrivalConfirmed ? 'arrivalConfirmed' : isConfirmed ? 'booked' : 'draft'), [
    isArrivalConfirmed,
    isConfirmed
  ]);

  const { firstName, lastName } = employeesMap?.[employeeId] || {};
  const name = useMemo(() => getEmployeeDisplayName(firstName, lastName, isShowLastNameFirst), [
    firstName,
    lastName,
    isShowLastNameFirst
  ]);

  const isEmpty = useCallback((value) => value === 'undefined' || value === null, []);

  const wrapperStyle = useMemo(
    () =>
      !isEmpty(startBucketNum) && !isEmpty(endBucketNum)
        ? {
            gridColumnStart: startBucketNum - offset + 2,
            gridColumnEnd: Math.min(endBucketNum - offset + 2, maxBucket),
            gridRowStart: 1,
            gridRowEnd: 1
          }
        : {},
    [endBucketNum, isEmpty, maxBucket, offset, startBucketNum]
  );

  const warning = useMemo(() => bookedOutsideAvailabilityByEmployee?.has(shiftId), [
    bookedOutsideAvailabilityByEmployee,
    shiftId
  ]);

  const onBookedShiftClick = useCallback(() => onDayClick({ ...data, employeeId, selectedBucket: index }), [
    data,
    employeeId,
    index,
    onDayClick
  ]);

  const shiftDontEndInCurrentDate = moment(endTime).isAfter(moment(`${date} 23:59:59`));
  const shiftDontStartInCurrentDate = moment(startTime).isBefore(moment(`${date} 00:00:00`));

  return (
    <>
      <Wrapper style={wrapperStyle} onClick={onBookedShiftClick}>
        <Tooltip body={`${name}, ${moment(startTime).format(timeFormat)} - ${moment(endTime).format(timeFormat)}`}>
          <Card
            ref={drag}
            $isDragging={isDragging}
            status={status}
            $shiftDontEndInCurrentDate={shiftDontEndInCurrentDate}
            $shiftDontStartInCurrentDate={shiftDontStartInCurrentDate}>
            <StatusLine status={status} $isDragging={isDragging} />

            <CardContent>
              <PositionDetails>
                {labelId && <Label $color={mappedLabels[labelId]?.color}> {mappedLabels[labelId].title}</Label>}
                {labelId && position && '-'}
                {position && <PositionLabel>{t(position.label)}</PositionLabel>}
                {isArrivalConfirmed && <ConfirmedIcon />}
              </PositionDetails>

              <Hours>
                {moment(startTime).format(timeFormat)} - {moment(endTime).format(timeFormat)}
              </Hours>
            </CardContent>

            {warning && (
              <Warning>
                <Tooltip body={t('bookedOutsideAvailability')}>
                  <CircleChip />
                </Tooltip>
              </Warning>
            )}
          </Card>
        </Tooltip>
      </Wrapper>
    </>
  );
};

export default React.memo(BookedRenderer);
